export type RetryOptions = {
  /** Number of retries after the first attempt. Default 2 (up to 3 attempts total). */
  retries?: number;
  /** Base delay for the exponential backoff. Default 300ms. */
  baseDelayMs?: number;
  /**
   * Set when repeating the request cannot change server state, e.g. a PUT of the same
   * bytes to a presigned URL. Treats the request like a safe method for retries.
   */
  idempotent?: boolean;
};

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const MAX_BACKOFF_EXPONENT = 5;

function abortReason(signal: AbortSignal | null | undefined): unknown {
  if (signal?.reason !== undefined) return signal.reason;
  return new DOMException('Aborted', 'AbortError');
}

function sleep(ms: number, signal?: AbortSignal | null): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortReason(signal));
      return;
    }

    const onAbort = () => {
      clearTimeout(timer);
      reject(abortReason(signal));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function backoffMs(attempt: number, baseDelayMs: number): number {
  const exponential = baseDelayMs * 2 ** Math.min(attempt, MAX_BACKOFF_EXPONENT);
  // Small +/-25% jitter avoids retry stampedes across tabs.
  return Math.round(exponential * (0.75 + Math.random() * 0.5));
}

function shouldRetryStatus(status: number, safeMethod: boolean): boolean {
  if (status === 429) return true;
  if (status >= 500) return safeMethod;
  return false;
}

/**
 * Single-host fetch with bounded retries.
 *
 * Retry policy (deliberate):
 * - The HTTP method is inferred from `init?.method ?? 'GET'`; GET/HEAD/OPTIONS are
 *   treated as safe methods.
 * - Retries when `fetch` rejects with a network error (e.g. `TypeError`) or when the
 *   response status is 429 or >= 500.
 * - Unsafe methods (POST/PUT/PATCH/DELETE) retry ONLY on 429, never on 5xx or network
 *   errors, because the mutation may already have been applied (student homework submits).
 * - Other 4xx statuses (400/401/403/404/409...) are never retried.
 * - The final `Response` is returned even when it is still 429/5xx after the last
 *   attempt, so the caller's own error handling stays in charge. If every attempt
 *   threw, the last error is rethrown.
 * - `init.signal` is respected: aborting stops immediately and rethrows the abort reason.
 */
export async function fetchWithRetry(
  input: RequestInfo | URL,
  init?: RequestInit,
  options?: RetryOptions,
): Promise<Response> {
  const retries = Math.max(0, options?.retries ?? 2);
  const baseDelayMs = Math.max(0, options?.baseDelayMs ?? 300);
  const method = (init?.method ?? 'GET').toUpperCase();
  const safeMethod = SAFE_METHODS.has(method) || options?.idempotent === true;
  const signal = init?.signal;

  if (signal?.aborted) throw abortReason(signal);

  for (let attempt = 0; attempt <= retries; attempt++) {
    let response: Response | null = null;
    try {
      response = await fetch(input, init);
    } catch (error) {
      if (signal?.aborted) throw error;
      // Mutations may already have been applied server-side, so a transport failure
      // is never retried for unsafe methods.
      if (!safeMethod || attempt >= retries) throw error;
      await sleep(backoffMs(attempt, baseDelayMs), signal);
      continue;
    }

    if (!shouldRetryStatus(response.status, safeMethod) || attempt >= retries) {
      return response;
    }
    await sleep(backoffMs(attempt, baseDelayMs), signal);
  }

  throw new Error('fetchWithRetry: no attempt was made');
}
